/**
 * inline_notes_controller.js — click-to-edit internal notes in place (Stimulus).
 *
 * Used by the shared notes card (components/detail/entity_notes.html) on
 * quote / job / invoice detail pages. The pencil (or the "Add internal notes"
 * empty state) swaps the read-only notes for the shared NotesEditor — the same
 * rich-text editor the edit modal uses (static/js/notes.js) — lazily mounted on
 * first edit. Save POSTs only `notes` through window.TurboEditModal; the update
 * endpoint's sentinels preserve every other field and re-render this whole card
 * as a Turbo Stream replace, which swaps back to display mode and reconnects a
 * fresh controller. Cancel restores the original content.
 *
 * An empty editor saves notes='' which CLEARS the notes (matches the edit
 * modal), so there is deliberately no empty-value guard here.
 *
 * One live editor at a time: participates in the shared coordination protocol
 * (inline_editor_coordination.js). Every notes value is submittable (empty
 * included), so this controller never vetoes a peer.
 *
 * Config (data-* on the controller root):
 *   data-inline-notes-url          POST target (the entity's update endpoint)
 *   data-inline-notes-entity-type  'quote' | 'job' | 'invoice' (toast label)
 *   data-inline-notes-entity-id    entity id, passed to NotesEditor.setEntity
 *
 * Markup contract inside the root:
 *   .inline-edit-display       read-only notes (+ attachments)
 *   .inline-edit-form          <form data-action="submit->inline-notes#save">
 *   .notes-editor              contenteditable mount point (unique id required)
 *   textarea[name="notes"]     hidden field; its initial value is the canon,
 *                              NotesEditor syncs editor HTML into it on input.
 */

import { Controller } from "@hotwired/stimulus";
import { bindEditorGuards, releaseHeldUpdate, requestOpen } from "./inline_editor_coordination.js";

export default class extends Controller {
        connect() {
            this.url = this.element.dataset.inlineNotesUrl;
            this.entityType = this.element.dataset.inlineNotesEntityType || 'item';
            this.entityId = this.element.dataset.inlineNotesEntityId;
            this.display = this.element.querySelector('.inline-edit-display');
            this.form = this.element.querySelector('.inline-edit-form');
            this.editorEl = this.form ? this.form.querySelector('.notes-editor') : null;
            this.textarea = this.form ? this.form.querySelector('textarea[name="notes"]') : null;
            this.original = this.textarea ? this.textarea.value : '';
            this.notesEditor = null;

            this._unbindGuards = bindEditorGuards(this.element, {
                isOpen: () => !!this.form && !this.form.classList.contains('d-none'),
                // NotesEditor mirrors editor HTML into the textarea on input,
                // so the textarea is current enough to answer "did it change".
                isDirty: () => !!this.textarea && this.textarea.value !== this.original,
                canCommit: () => true,
                commit: () => this.save(),
                close: () => this._showDisplay(),
            });
        }

        disconnect() {
            if (this._unbindGuards) {
                this._unbindGuards();
                this._unbindGuards = null;
            }
        }

        edit() {
            if (!this.form || !this.editorEl || !this.textarea) return;
            if (!requestOpen(this.element)) return;
            this._mountEditor();
            this.display.classList.add('d-none');
            this.form.classList.remove('d-none');
            this.editorEl.focus();
        }

        // Lazily instantiate the shared NotesEditor against this card's editor
        // div + hidden textarea. setContent seeds the editor from the textarea
        // (the canon) and syncs back, so Save works even with no further input.
        _mountEditor() {
            if (!this.notesEditor && typeof window.NotesEditor !== 'undefined') {
                this.notesEditor = new window.NotesEditor(this.editorEl.id, this.textarea.id, null);
                if (this.entityId) this.notesEditor.setEntity(this.entityType, this.entityId);
            }
            if (this.notesEditor) {
                this.notesEditor.setContent(this.original);
            } else {
                // NotesEditor unavailable: degrade to a plain contenteditable
                // div so the field is still editable.
                this.editorEl.contentEditable = true;
                this.editorEl.innerHTML = this.original;
            }
        }

        cancel() {
            this.textarea.value = this.original;
            if (this.editorEl) this.editorEl.innerHTML = this.original;
            this._showDisplay();
            releaseHeldUpdate(this.element);
        }

        async save(event) {
            if (event) event.preventDefault();
            if (!this.textarea || !window.TurboEditModal) return;
            // Belt and braces: NotesEditor syncs on input, but a programmatic
            // change (e.g. execCommand without an input event) must not be lost.
            if (this.notesEditor) {
                this.notesEditor.syncContent();
            } else if (this.editorEl) {
                this.textarea.value = this.editorEl.innerHTML;
            }
            // A formError stream targets the modal's hidden error region —
            // invisible here — so surface it as a toast instead and leave the
            // editor open for retry.
            await window.TurboEditModal.submit(this.form, {
                entityType: this.entityType,
                url: this.url,
                onError: (err) => {
                    if (err && err.formError) {
                        window.TurboEditModal.showNotification(
                            'Those notes were not accepted. Please try again.',
                            'error'
                        );
                    }
                },
            });
        }

        _showDisplay() {
            if (this.form) this.form.classList.add('d-none');
            if (this.display) this.display.classList.remove('d-none');
        }
}
